import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "sonner";

import Home from "@/pages/Home";
import EmployeeDetail from "@/pages/EmployeeDetail";
import Pricing from "@/pages/Pricing";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import { Privacy, Terms } from "@/pages/Legal";
import CheckEmail from "@/pages/CheckEmail";
import VerifyMagicLink from "@/pages/VerifyMagicLink";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Employees from "@/pages/Employees";
import EmployeeCommand from "@/pages/EmployeeCommand";
import Tasks from "@/pages/Tasks";
import Approvals from "@/pages/Approvals";
import Activity from "@/pages/Activity";
import Integrations from "@/pages/Integrations";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import Billing from "@/pages/Billing";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";
import { Documents, Help } from "@/pages/Misc";

function Private({ children, requireOnboarding = true }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-ink" />;
  if (!user) return <Navigate to="/login" replace />;
  if (requireOnboarding && !user.onboarding_complete) return <Navigate to="/onboarding" replace />;
  return children;
}

function AdminGuard({ children }) {
  const token = typeof window !== "undefined" ? localStorage.getItem("hired_admin_token") : null;
  if (!token) return <Navigate to="/admin/login" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" theme="dark" toastOptions={{
          style: { background: "#F6F2E9", color: "#0B0D10", borderRadius: 0, border: "1px solid #D4AF37", fontWeight: 500 }
        }} />
        <Routes>
          {/* Public */}
          <Route path="/" element={<Home />} />
          <Route path="/hired/:slug" element={<EmployeeDetail />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/check-email" element={<CheckEmail />} />
          <Route path="/auth/verify" element={<VerifyMagicLink />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />

          {/* Auth-protected but pre-onboarding */}
          <Route path="/onboarding" element={<Private requireOnboarding={false}><Onboarding /></Private>} />

          {/* Payment result screens (available after onboarding) */}
          <Route path="/payment/success" element={<Private><PaymentSuccess /></Private>} />
          <Route path="/payment/cancel" element={<Private><PaymentCancel /></Private>} />

          {/* Post-onboarding dashboard */}
          <Route path="/dashboard" element={<Private><Dashboard /></Private>} />
          <Route path="/employees" element={<Private><Employees /></Private>} />
          <Route path="/employees/:id" element={<Private><EmployeeCommand /></Private>} />
          <Route path="/tasks" element={<Private><Tasks /></Private>} />
          <Route path="/approvals" element={<Private><Approvals /></Private>} />
          <Route path="/activity" element={<Private><Activity /></Private>} />
          <Route path="/integrations" element={<Private><Integrations /></Private>} />
          <Route path="/documents" element={<Private><Documents /></Private>} />
          <Route path="/reports" element={<Private><Reports /></Private>} />
          <Route path="/billing" element={<Private><Billing /></Private>} />
          <Route path="/settings" element={<Private><Settings /></Private>} />
          <Route path="/help" element={<Private><Help /></Private>} />

          {/* Admin */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminGuard><AdminDashboard /></AdminGuard>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
